import { useMemo, useState } from "react";
import { Check, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AVATAR_COLORS, AVATAR_ICONS, avatarColorVar } from "@/lib/avatars";
import { cn } from "@/lib/utils";

/**
 * Picks an agent's avatar: a search over the icon keys, a grid of the curated
 * icons drawn in the chosen tint, and the tint swatches. Inline rather than a
 * popover, so on a phone it scrolls with the dialog instead of floating over the
 * keyboard. `icon` null means "let the server pick", which is what a new agent
 * gets until the user chooses.
 */
export function IconPicker({
  icon,
  color,
  onChange,
}: {
  icon: string | null;
  color: string | null;
  onChange: (next: { icon: string | null; color: string | null }) => void;
}) {
  const [query, setQuery] = useState("");
  const keys = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, "-");
    const all = Object.keys(AVATAR_ICONS);
    return q ? all.filter((key) => key.includes(q)) : all;
  }, [query]);
  const tint = avatarColorVar(color) ?? "var(--primary)";

  return (
    <div className="space-y-2.5 rounded-md border border-border/70 p-2.5">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          placeholder="Search icons"
          aria-label="Search icons"
          className="h-8 pl-8 text-[13px]"
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div role="listbox" aria-label="Icon" className="grid max-h-44 grid-cols-8 gap-1 overflow-y-auto scrollbar-slim">
        {keys.map((key) => {
          const Icon = AVATAR_ICONS[key]!;
          const selected = key === icon;
          return (
            <button
              key={key}
              type="button"
              role="option"
              aria-selected={selected}
              aria-label={key.replace(/-/g, " ")}
              title={key.replace(/-/g, " ")}
              onClick={() => onChange({ icon: key, color })}
              className={cn(
                "grid aspect-square place-items-center rounded-md transition-colors",
                selected ? "ring-2 ring-inset" : "hover:bg-muted/60",
              )}
              style={
                selected
                  ? {
                      color: tint,
                      backgroundColor: `color-mix(in oklab, ${tint} 16%, transparent)`,
                      // @ts-expect-error CSS custom property for the ring color
                      "--tw-ring-color": tint,
                    }
                  : { color: "var(--muted-foreground)" }
              }
            >
              <Icon className="size-4" />
            </button>
          );
        })}
        {keys.length === 0 ? <p className="col-span-8 py-3 text-center text-[12px] text-muted-foreground">No icon matches.</p> : null}
      </div>
      <div role="radiogroup" aria-label="Colour" className="flex flex-wrap gap-1.5">
        {AVATAR_COLORS.map((key) => {
          const selected = key === color;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={key}
              title={key}
              onClick={() => onChange({ icon, color: key })}
              className={cn(
                "grid size-6 place-items-center rounded-full ring-offset-2 ring-offset-background transition-transform hover:scale-110",
                selected && "ring-2 ring-ring",
              )}
              style={{ backgroundColor: `var(--avatar-${key})` }}
            >
              {selected ? <Check className="size-3.5 text-background" strokeWidth={3} /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
